import { useEffect, useMemo, useRef, useState } from 'react';
import { endOfMonth, endOfWeek, endOfYear, format, startOfMonth, startOfToday, startOfWeek, startOfYear, subDays, subMonths, subWeeks } from 'date-fns';
import { ru } from 'date-fns/locale';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Separator } from './ui/separator';
import { cn } from '../lib/utils';
import { trackEvent } from '../lib/usageEvents';
import { useIsMobile } from '../hooks/useMediaQuery';
import { clearButtonLabel, isRangeSelectionComplete, periodToCommit, resolveClear } from './dateRangeSelection';

/**
 * `getDefaultValue` — период по умолчанию владельца календаря. С ним кнопка очистки возвращает
 * к нему («Сбросить»), без него — оставляет период невыбранным («Очистить»).
 */
const DateRangePicker = ({ value, onChange, getDefaultValue, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const [selectionRange, setSelectionRange] = useState(() => ({
    startDate: value?.from ?? startOfToday(),
    endDate: value?.to ?? startOfToday(),
    key: 'selection'
  }));
  // Черновик из календаря, ещё не отданный наружу. Ref, а не только состояние: библиотека зовёт
  // `onChange` и `onRangeFocusChange` одного клика подряд, и до второго вызова новое состояние
  // ещё не применено.
  const draftRef = useRef(null);

  useEffect(() => {
    if (value?.from && value?.to) {
      setSelectionRange({ startDate: value.from, endDate: value.to, key: 'selection' });
    }
  }, [value]);

  const presetRanges = useMemo(
    () => [
      {
        id: 'today',
        label: 'Сегодня',
        range: () => {
          const today = startOfToday();
          return { startDate: today, endDate: today };
        }
      },
      {
        id: 'yesterday',
        label: 'Вчера',
        range: () => {
          const yesterday = subDays(startOfToday(), 1);
          return { startDate: yesterday, endDate: yesterday };
        }
      },
      {
        id: 'current_week',
        label: 'Текущая неделя',
        range: () => {
          const start = startOfWeek(new Date(), { weekStartsOn: 1 });
          return { startDate: start, endDate: endOfWeek(start, { weekStartsOn: 1 }) };
        }
      },
      {
        id: 'previous_week',
        label: 'Предыдущая неделя',
        range: () => {
          const start = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
          return { startDate: start, endDate: endOfWeek(start, { weekStartsOn: 1 }) };
        }
      },
      {
        id: 'current_month',
        label: 'Текущий месяц',
        range: () => {
          const start = startOfMonth(new Date());
          return { startDate: start, endDate: endOfMonth(start) };
        }
      },
      {
        id: 'previous_month',
        label: 'Предыдущий месяц',
        range: () => {
          const start = startOfMonth(subMonths(new Date(), 1));
          return { startDate: start, endDate: endOfMonth(start) };
        }
      },
      {
        id: 'current_year',
        label: 'Текущий год',
        range: () => {
          const start = startOfYear(new Date());
          return { startDate: start, endDate: endOfYear(start) };
        }
      }
    ],
    []
  );

  const getDateRangeText = () => {
    const from = selectionRange?.startDate;
    const to = selectionRange?.endDate;
    if (!from || !to) return 'Выберите период';
    if (format(from, 'yyyy-MM-dd') === format(to, 'yyyy-MM-dd')) {
      return format(from, 'd MMM yyyy', { locale: ru });
    }
    return `${format(from, 'd MMM', { locale: ru })} - ${format(to, 'd MMM yyyy', { locale: ru })}`;
  };

  const applyRange = (range) => {
    const normalized = { ...range, key: 'selection' };
    draftRef.current = null;
    setSelectionRange(normalized);
    onChange?.({ from: normalized.startDate, to: normalized.endDate });
    setIsOpen(false);
  };

  const commitDraft = () => {
    const period = periodToCommit(draftRef.current, value);
    draftRef.current = null;
    if (period) {
      onChange?.(period);
      trackEvent('period.range.apply');
    }
  };

  // Наружу клик по дню не уходит: первый клик даёт диапазон из одного дня, и раньше этот день
  // успевал уехать во все графики раздела раньше, чем пользователь выберет конец периода.
  const handleRangeChange = (ranges) => {
    draftRef.current = ranges.selection;
    setSelectionRange(ranges.selection);
  };

  const handleRangeFocusChange = (focusedRange) => {
    if (isRangeSelectionComplete(focusedRange)) {
      commitDraft();
    }
  };

  // Закрытие любым способом — «Готово», клик мимо, Esc — применяет недовыбранный черновик:
  // один клик и закрытие дают, как и раньше, один день. Radix зовёт `onOpenChange` только на
  // действия пользователя, поэтому «Готово» идёт сюда же явно.
  const handleOpenChange = (open) => {
    if (!open) {
      commitDraft();
    }
    setIsOpen(open);
  };

  const handleClear = () => {
    draftRef.current = null;
    const { period, changed } = resolveClear(getDefaultValue, value);
    setSelectionRange(period ? { startDate: period.from, endDate: period.to, key: 'selection' } : undefined);
    if (changed) {
      onChange?.(period);
    }
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-ev="period.range.toggle"
          className={cn(
            'w-full justify-start text-left font-normal sm:w-[320px]',
            !selectionRange?.startDate && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDateRangeText()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[360px] sm:w-[900px] max-w-[calc(100vw-24px)] max-h-[80vh] overflow-auto max-sm:max-w-[calc(100vw-32px)]"
        align="center"
        sideOffset={10}
        collisionPadding={16}
      >
        <div className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-56 border-b sm:border-b-0 sm:border-r bg-muted/40 p-3 space-y-2 max-sm:grid max-sm:grid-cols-2 max-sm:gap-1 max-sm:space-y-0 max-sm:p-2">
            {presetRanges.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                data-ev="period.preset.select"
                data-ev-value={preset.id}
                onClick={() => applyRange(preset.range())}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div className="flex-1 p-3 max-sm:p-2">
            {/* На телефоне месяц растягивается по ширине поповера: библиотечные 27.667em
                (≈332px) не помещаются даже одним месяцем. */}
            <div className="overflow-x-auto pb-1 max-sm:[&_.rdrCalendarWrapper]:w-full max-sm:[&_.rdrMonth]:w-full">
              <DateRange
                locale={ru}
                ranges={[selectionRange ?? { startDate: startOfToday(), endDate: startOfToday(), key: 'selection' }]}
                onChange={handleRangeChange}
                onRangeFocusChange={handleRangeFocusChange}
                months={isMobile ? 1 : 2}
                direction="horizontal"
                showDateDisplay={false}
                weekStartsOn={1}
              />
            </div>
            <Separator className="my-3" />
            <div className="flex items-center justify-between space-x-2">
              <Button variant="ghost" size="sm" data-ev={getDefaultValue ? 'period.range.reset' : 'period.range.clear'} onClick={handleClear}>
                {clearButtonLabel(getDefaultValue)}
              </Button>
              <Button size="sm" data-ev="period.range.done" onClick={() => handleOpenChange(false)} disabled={!selectionRange?.startDate || !selectionRange?.endDate}>
                Готово
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
